/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect } from 'react';
import Masonry from 'react-masonry-css';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import Container from '@mui/material/Container';
import Box from '@mui/material/Box';

import MediaCarousel from 'src/components/ui/MediaCarousel';
import PostCard from 'src/components/news/posts/PostCard';
import AspectRatio from 'src/components/ui/AspectRatio';
import Loader from 'src/components/ui/Loader';
import CompanySeeAllHeader from 'src/components/company/CompanySeeAllHeader';
import FullWidthLayout from 'src/components/other/FullWidthLayout';

import { colours } from 'src/constants/colours';

import { usePostsQuery, useCompanyProfileCountsQuery } from 'src/hooks/__generated__/queries';

import type { PostsQuery, Post, Medium } from 'src/types/__generated__/graphql';
import useAppRouter from 'src/hooks/useAppRouter';
import { styledComponent } from 'src/utils/styled';

const StyledMasonry = styledComponent(Masonry)`
    display: flex;
    .my-masonry-grid {
        display: flex;
        margin-left: -1.5rem; /* gutter size offset */
    }
    .my-masonry-grid_column:not(:first-of-type) {
        padding-left: 1.5rem; /* gutter size */
    }
    /* Style your items */
    .my-masonry-grid_column > div {
        /* change div to reference your elements you put in <Masonry> */
        margin-bottom: 1.5rem;
    }
`;

const CompanyPostPage: PageComponent = () => {
    const router = useAppRouter();

    const companyId = router.query.companyId as string;
    const defaultLimit = 12;

    const { previousData, data: profile = previousData } = useCompanyProfileCountsQuery({
        variables: {
            companyId: companyId as string,
        },
        skip: !companyId,
    });

    const {
        previousData: companyPostsPreviousData,
        data: companyPosts = companyPostsPreviousData,
        fetchMore: fetchMoreCompanyPosts,
        loading,
    } = usePostsQuery({
        variables: {
            skip: 0,
            companyId,
            take: defaultLimit,
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const handleScroll = async () => {
        const bottom =
            Math.round(window.pageYOffset + window.innerHeight + 80) > Math.round(document.body.scrollHeight); // 80 = ~ footer min height
        const postsLength = companyPosts?.posts?.length ?? 0;
        const totalPosts = profile?.companyProfile.numberOfPosts ?? 0;
        const allDataLoaded = postsLength >= totalPosts;
        if (bottom && !allDataLoaded && !loading) {
            await fetchMoreCompanyPosts({
                variables: {
                    skip: postsLength,
                },
            });
        }
    };
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, companyPosts?.posts?.length, profile?.companyProfile.numberOfPosts]);

    const postList: React.ReactNode = companyPosts?.posts?.map((post: ArrayElement<PostsQuery['posts']>) => {
        const images = () => (
            <AspectRatio ratio={3 / 1}>
                <MediaCarousel
                    media={post.media as Medium[]}
                    alt={String(post.message)}
                    objectFit="contain"
                    zoomableImages
                />
            </AspectRatio>
        );
        return (
            <Box
                key={post.id}
                data-target="post-list"
                data-target-id={`post-list-item-id-${post.id}`}
                mb={2}
                mr={2}
                width="100%"
            >
                <PostCard
                    carousel={images}
                    showHeader={false}
                    post={post as Post}
                    backgroundColor={colours.black.O04}
                />
            </Box>
        );
    });

    return (
        <Container>
            <CompanySeeAllHeader titleTranslationKey="profile:publications" />
            <Box mt={3}>
                <StyledMasonry
                    breakpointCols={{
                        default: 3,
                    }}
                    className="my-masonry-grid"
                    columnClassName="my-masonry-grid_column"
                >
                    {postList ?? null}
                </StyledMasonry>
                {loading ? (
                    <Box height="3rem" width="100%" mt="1rem">
                        <Loader />
                    </Box>
                ) : null}
            </Box>
        </Container>
    );
};

CompanyPostPage.Layout = FullWidthLayout;

export const getServerSideProps = async ({ locale }: { locale: string }) => ({
    props: {
        ...(await serverSideTranslations(locale, ['common', 'profile', 'company', 'ad'])),
    },
});

export default CompanyPostPage;
